import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ModuleHeader from "../components/shared/ModuleHeader";
import { timelineApi } from "../services/api";

const IMG_TIMELINE = "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1400&q=75&auto=format&fit=crop";

const MODULE_META = {
  food: { label: "Food", icon: "🥬", color: "#ff6b6b" },
  gifts: { label: "Gifts", icon: "🎁", color: "#ff4d4d" },
  trips: { label: "Trips", icon: "✈️", color: "#8b5cf6" },
  events: { label: "Events", icon: "📅", color: "#818cf8" },
  vault: { label: "Vault", icon: "🏠", color: "#64748b" },
  vehicles: { label: "Vehicles", icon: "🚗", color: "#f59e0b" },
  subs: { label: "Subs", icon: "🔁", color: "#0891b2" },
  utilities: { label: "Utilities", icon: "💡", color: "#0f766e" },
  household: { label: "Household", icon: "◈", color: "#475569" },
  discoveries: { label: "Discoveries", icon: "✦", color: "#0891b2" },
  cases: { label: "Cases", icon: "◇", color: "#0f766e" },
  predictions: { label: "Predictions", icon: "↗", color: "#7c3aed" },
};

const PRIORITY_META = {
  urgent: { label: "Urgent", color: "#b91c1c", bg: "rgba(185,28,28,.10)" },
  high: { label: "High", color: "#b45309", bg: "rgba(180,83,9,.10)" },
  soon: { label: "Soon", color: "#0f766e", bg: "rgba(15,118,110,.10)" },
  savings: { label: "Save", color: "#047857", bg: "rgba(4,120,87,.10)" },
  upcoming: { label: "Upcoming", color: "#64748b", bg: "rgba(100,116,139,.10)" },
  fyi: { label: "FYI", color: "#64748b", bg: "rgba(100,116,139,.10)" },
};

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, days) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function rangeForMode(mode) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (mode === "week") return { start: isoDate(today), end: isoDate(addDays(today, 6)), days: 7 };
  if (mode === "history") return { start: isoDate(addDays(today, -29)), end: isoDate(today), days: 30 };
  if (mode === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: isoDate(start), end: isoDate(end), days: end.getDate() };
  }
  return { start: isoDate(today), end: isoDate(addDays(today, 29)), days: 30 };
}

function dayLabel(value) {
  const date = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

function groupByDate(items) {
  return (items || []).reduce((acc, item) => {
    const key = item.date || "unscheduled";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

function TimelineItem({ item, onOpen }) {
  const module = MODULE_META[item.module] || MODULE_META.household;
  const priority = PRIORITY_META[item.priority] || PRIORITY_META.upcoming;
  return (
    <button
      type="button"
      className="hm-life-item"
      onClick={() => item.action_url && onOpen(item.action_url)}
      disabled={!item.action_url}
      style={{ "--timeline-color": module.color }}
    >
      <span className="hm-life-item__icon" aria-hidden="true">{module.icon}</span>
      <span className="hm-life-item__body">
        <span className="hm-life-item__title">{item.title}</span>
        {item.subtitle && <span className="hm-life-item__subtitle">{item.subtitle}</span>}
      </span>
      <span className="hm-life-item__meta">
        {item.is_overdue
          ? <em>Overdue</em>
          : <strong style={{ color: priority.color, background: priority.bg }}>{priority.label}</strong>
        }
        {item.lifecycle_label && <small className="hm-life-item__state">{item.lifecycle_label}</small>}
        <small>{module.label}</small>
      </span>
    </button>
  );
}

export default function TimelinePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMode = searchParams.get("range") || "week";
  const mode = ["week", "rolling", "month", "history"].includes(requestedMode) ? requestedMode : "week";
  const actionOnly = searchParams.get("actions") === "1";
  const moduleFilter = searchParams.get("module") || "all";
  const showCompleted = searchParams.get("completed") === "1";
  const [payload, setPayload] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => rangeForMode(mode), [mode]);
  const updateView = (key, value, defaultValue = "") => {
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      if (value === defaultValue || value === false) next.delete(key);
      else next.set(key, value === true ? "1" : String(value));
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      timelineApi.list({ start: range.start, end: range.end, action_only: actionOnly ? "1" : "" }),
      timelineApi.summary({ start: range.start, days: Math.min(range.days, 30) }),
    ])
      .then(([items, summaryPayload]) => {
        if (!alive) return;
        setPayload(items);
        setSummary(summaryPayload);
      })
      .catch(() => {
        if (!alive) return;
        setPayload({ items: [], count: 0 });
        setSummary(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [actionOnly, range.days, range.end, range.start]);

  const allItems = payload?.items || [];
  const isCompletedLifecycle = item => item.is_completed === true;
  const completedCount = mode === "history" ? allItems.filter(isCompletedLifecycle).length : 0;
  const visibleItems = allItems.filter(item => (
    (moduleFilter === "all" || item.module === moduleFilter)
    && (mode !== "history" || showCompleted || !isCompletedLifecycle(item))
  ));
  const groups = groupByDate(visibleItems);
  const dates = Object.keys(groups).sort();
  const moduleCounts = summary?.modules || {};
  const openTarget = (url) => {
    if (/^https?:/i.test(url)) {
      window.location.assign(url);
      return;
    }
    navigate(url.startsWith("/app") ? url : `/app${url.startsWith("/") ? url : `/${url}`}`);
  };

  return (
    <div className="hm-life-page">
      <ModuleHeader
        title="Life Timeline"
        subtitle="One verified history of what is due, discovered, forecast, decided, and resolved across your household."
        image={IMG_TIMELINE}
      >
        <span>{payload?.count || 0} items</span>
        <span>{summary?.due_today || 0} today</span>
        <span>{summary?.overdue || 0} overdue</span>
      </ModuleHeader>

      <section className="hm-life-toolbar">
        <div className="hm-life-tabs" role="tablist" aria-label="Timeline range">
          {[
            ["week", "Week"],
            ["rolling", "30 days"],
            ["month", "Month"],
            ["history", "Past"],
          ].map(([value, label]) => (
            <button key={value} type="button" className={mode === value ? "is-active" : ""} onClick={() => updateView("range", value, "week")}>
              {label}
            </button>
          ))}
        </div>
        <label className="hm-life-toggle">
          <input type="checkbox" checked={actionOnly} onChange={event => updateView("actions", event.target.checked)} />
          <span>Actions only</span>
        </label>
      </section>

<section className="hm-life-filters" aria-label="Timeline filters">
        <button
          type="button"
          className={moduleFilter === "all" ? "is-active" : ""}
          onClick={() => updateView("module", "all", "all")}
        >
          All <strong>{payload?.count || 0}</strong>
        </button>
        {Object.entries(MODULE_META).filter(([key]) => moduleCounts[key]).map(([key, meta]) => (
          <button
            key={key}
            type="button"
            className={`hm-life-filter-chip${moduleFilter === key ? " is-active" : ""}`}
            style={{ "--timeline-color": meta.color }}
            onClick={() => updateView("module", key, "all")}
          >
            {meta.icon} {meta.label} <strong>{moduleCounts[key]}</strong>
          </button>
        ))}
        {mode === "history" && completedCount > 0 && (
          <button
            type="button"
            className={`hm-life-filters__completed${showCompleted ? " is-active" : ""}`}
            onClick={() => updateView("completed", !showCompleted)}
          >
            {showCompleted ? "Hide completed" : `${completedCount} completed`}
          </button>
        )}
      </section>

      {loading && (
        <section className="hm-life-loading">
          <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          Loading timeline...
        </section>
      )}

      {!loading && dates.length === 0 && (
        <section className="hm-life-empty">
          <strong>{moduleFilter !== "all" || actionOnly || (mode === "history" && !showCompleted) ? "Nothing matches these filters." : "Nothing scheduled in this range."}</strong>
          <span>{mode === "history" && completedCount > 0 && !showCompleted ? `${completedCount} completed lifecycle item${completedCount === 1 ? " is" : "s are"} folded away. Use Show completed to review the audit trail.` : "HouseMind will add dated household items here as modules fill in."}</span>
        </section>
      )}

      {!loading && dates.length > 0 && (
        <section className="hm-life-list">
          {dates.map(date => (
            <article key={date} className={`hm-life-day${date === isoDate(new Date()) ? " is-today" : ""}`}>
              <div className="hm-life-day__head">
                <strong>{dayLabel(date)}</strong>
                <span>{groups[date].length} item{groups[date].length === 1 ? "" : "s"}</span>
              </div>
              <div className="hm-life-day__items">
                {groups[date].map(item => (
                  <TimelineItem key={`${item.date}-${item.module}-${item.type}-${item.source_id}-${item.title}`} item={item} onOpen={openTarget} />
                ))}
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
